/**
 * Column model + row coercion for the classifier-v3 ``line_items`` (Buchungen)
 * repeating-row table (#1525).
 *
 * A v3 bank-statement cell surfaces ``line_items`` as a raw array of row objects
 * (``{ date, booking_date, description, reference, amount, balance }`` — keys per
 * ``config/taxonomy-v2/finance/bank-doc.yaml:263-274``, all optional, values are
 * verbatim LLM-extracted strings). ``DocumentsPage`` attaches this column model +
 * the array to the fact's ``rows``/``columns`` so ``DocumentPreview`` renders a
 * compact, non-editable table instead of the ``"N Positionen"`` scalar summary.
 *
 * The column *labels* are the German headers from the bank-doc cell schema
 * (``:110`` → "Buchungen"); the design system stays domain-agnostic and receives
 * them via ``columns[].label`` (mirrors how ``classification``/``taxonomyDomains``
 * are passed in).
 */
import type { PreviewFactColumn } from '@hofsync/design-system';

/** Bank-statement Buchungen column order + German headers. ``amount``/``balance``
 *  are right-aligned (``numeric``); their values are pre-formatted German strings
 *  rendered verbatim (no reparse). */
export const LINE_ITEMS_COLUMNS: PreviewFactColumn[] = [
    { key: 'date', label: 'Datum' },
    { key: 'booking_date', label: 'Buchungsdatum' },
    { key: 'description', label: 'Buchungstext' },
    { key: 'reference', label: 'Referenz' },
    { key: 'amount', label: 'Betrag', numeric: true },
    { key: 'balance', label: 'Saldo', numeric: true },
];

type LineItemRow = Record<string, string | null | undefined>;

/** Field id of the repeating-row cell. ``line_items`` is shared across many v3
 *  cells (bank-doc → Buchungen, finance/invoice → Positionen, etc.), each with its
 *  own row shape — so the field name alone does NOT pin the bank column model. */
export const LINE_ITEMS_FIELD = 'line_items';

/** The classifier-v3 cell whose ``line_items`` row shape matches LINE_ITEMS_COLUMNS
 *  ({ date, booking_date, description, reference, amount, balance }). Only this
 *  (domain, role) gets the Buchungen table; other cells' ``line_items`` (different
 *  shapes/labels) keep the scalar "N Positionen" summary (#1525 codex P1). */
export const BANK_DOC_DOMAIN = 'finance';
export const BANK_DOC_ROLE = 'bank-doc';

/**
 * Narrow an extracted-fact ``value`` to the repeating-row table model. Returns the
 * coerced rows when ``value`` is an array (each cell stringified verbatim, ``null``
 * preserved so the table renders an empty cell), or ``null`` for any scalar value —
 * only array-valued facts become tables; scalar facts are unchanged.
 */
export function lineItemsRows(value: string | unknown[] | null | undefined): LineItemRow[] | null {
    if (!Array.isArray(value)) return null;
    return value.map((row) => {
        const out: LineItemRow = {};
        if (row && typeof row === 'object') {
            for (const [key, cell] of Object.entries(row as Record<string, unknown>)) {
                // Preserve null/undefined so the renderer shows an empty cell; stringify
                // everything else verbatim (LLM strings, or a stray number — never
                // "[object Object]").
                out[key] = cell === null || cell === undefined ? (cell as null | undefined) : String(cell);
            }
        }
        return out;
    });
}

/**
 * Build the repeating-row table extras (``rows`` + bank ``columns``) for an extracted
 * fact, or ``null`` when the fact should keep its scalar rendering. Gated to the
 * bank-statement cell (``finance``/``bank-doc``) ``line_items`` field — that is the
 * only cell whose row shape matches LINE_ITEMS_COLUMNS. ``line_items`` on any other
 * cell (finance/invoice → "Positionen", livestock/*, fields/*, …) has a different
 * shape/label, so it falls through to the scalar "N Positionen" summary rather than
 * rendering with the wrong (mislabelled) bank columns (#1525 codex P1).
 */
export function buildLineItemsTableExtras(
    field: string,
    value: string | unknown[] | null | undefined,
    classification?: { domain?: string | null; role?: string | null } | null,
): { rows: LineItemRow[]; columns: PreviewFactColumn[] } | null {
    if (field !== LINE_ITEMS_FIELD) return null;
    if (classification?.domain !== BANK_DOC_DOMAIN || classification?.role !== BANK_DOC_ROLE) {
        return null;
    }
    const rows = lineItemsRows(value);
    if (!rows) return null;
    return { rows, columns: LINE_ITEMS_COLUMNS };
}
